<script setup>
import { computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import { useWhatsappErrorTranslation } from 'dashboard/composables/useWhatsappErrorTranslation';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMessageContext } from './provider.js';
import { ORIENTATION } from './constants';

const props = defineProps({
  error: { type: String, required: true },
});

const { orientation } = useMessageContext();

const { t } = useI18n();
const { translateError } = useWhatsappErrorTranslation();
const { isAWhatsAppChannel } = useInbox();

const ERROR_TUTORIAL_CODES = ['131049', '63024', '63049', '63032', '131042'];

const tutorialUrl = computed(() => {
  const matchedCode = ERROR_TUTORIAL_CODES.find(code =>
    new RegExp(`\\b${code}\\b`).test(String(props.error))
  );

  return matchedCode ? t(`CHAT_LIST.ERROR_TUTORIAL_URLS.${matchedCode}`) : null;
});

const translatedError = computed(() => {
  if (!isAWhatsAppChannel.value) return props.error;

  return translateError(props.error, { preserveUnknownDetails: true });
});
</script>

<template>
  <div class="flex flex-col gap-1.5 text-xs text-n-ruby-11">
    <div class="flex items-center gap-1.5">
      <a
        v-if="tutorialUrl"
        :href="tutorialUrl"
        :aria-label="t('CHAT_LIST.ERROR_TUTORIAL_ARIA_LABEL')"
        class="inline-flex size-5 items-center justify-center rounded-md bg-n-alpha-2 text-n-slate-10 transition-colors hover:bg-n-alpha-3 hover:text-n-brand"
        rel="noreferrer noopener"
        target="_blank"
      >
        <Icon icon="i-lucide-info" class="size-[14px]" />
      </a>
      <span>{{ t('CHAT_LIST.FAILED_TO_SEND') }}</span>
      <div class="relative group">
        <div
          class="bg-n-alpha-2 rounded-md size-5 grid place-content-center cursor-pointer"
        >
          <Icon
            icon="i-lucide-alert-triangle"
            class="text-n-ruby-11 size-[14px]"
          />
        </div>
        <div
          class="absolute bg-n-alpha-3 px-4 py-3 border rounded-md border-n-strong text-n-slate-12 bottom-6 w-52 text-xs backdrop-blur-[100px] shadow-[0px_0px_24px_0px_rgba(0,0,0,0.12)] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all break-all"
          :class="{
            'ltr:left-0 rtl:right-0': orientation === ORIENTATION.LEFT,
            'ltr:right-0 rtl:left-0': orientation === ORIENTATION.RIGHT,
          }"
        >
          {{ translatedError }}
        </div>
      </div>
    </div>
  </div>
</template>
